/**
 * Reconciliation read models.
 *
 * ⚠️ These are fixtures, not an API client. The bank read API and the ledger read
 * API do not exist yet, so this module stands in for both while the screen's
 * layout is settled.
 *
 * The types below are deliberately shaped as the responses those two endpoints are
 * specified to return, so that replacing the fixtures with live data changes the
 * body of `getBankDays` and `getLedgerEntries` and nothing else. The shapes are a
 * proposal to the people building the endpoints, not a settled contract.
 *
 * Two rules hold throughout and are not negotiable downstream:
 *
 *  - **Amounts are strings.** Never a number, never minor units. Bank and ledger
 *    amounts are exact decimals and a float conversion silently loses precision.
 *  - **Absent is not zero.** One account never reports a balance in any file, so
 *    `amount: null` means the figure is not in the feed. Rendering it as 0.00
 *    would state something the data does not say.
 */

/** Inclusive date range, both bounds as `YYYY-MM-DD`. */
export interface DateRange {
  from: string;
  to: string;
}

export interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface Paginated<T> {
  data: T[];
  pagination: PaginationData;
}

/**
 * Whether the bank's file for a given day arrived and could be read.
 *
 * A day carries its state rather than being absent from the list, because "the
 * bank sent nothing" and "we never received anything" are different answers and
 * an operator has to be able to tell them apart. Labels on screen are the
 * screen's choice; these three values are the contract.
 */
export type BankDayState =
  /** A file arrived and parsed. It may legitimately contain no transactions. */
  | 'present'
  /** No file arrived, and the hour one was expected by has passed. */
  | 'not_delivered'
  /** A file arrived and could not be parsed. */
  | 'delivered_not_parsed';

/** BAI2 balance type codes carry an amount only sometimes; absent stays absent. */
export interface BankBalanceDto {
  /** BAI2 type code, e.g. `010` opening ledger, `015` closing ledger. */
  typeCode: string;
  /**
   * What the code means, or `null` where no meaning is established. CASS still
   * owes us `030`, `059` and `060`, so a label cannot be invented for them.
   */
  label: string | null;
  /** Exact decimal string, or `null` when the feed carries no figure. */
  amount: string | null;
}

export interface BankTransactionDto {
  id: string;
  /**
   * Derived from the type code, not stored: 100 to 399 credit, 400 to 699 debit.
   * `null` when the code cannot be placed, because a guessed direction still
   * looks like an answer and would silently corrupt any sum taken over it.
   */
  direction: 'credit' | 'debit' | null;
  /** BAI2 type code, e.g. `142`, `451`. */
  typeCode: string;
  /** What the code means, or `null` where no meaning is established. */
  typeLabel: string | null;
  /** Exact decimal string, always positive; `direction` carries the sign. */
  amount: string;
  customerReference: string;
  text: string;
  /**
   * Whether this movement has been matched to a ledger entry. Nothing writes it
   * yet: persisting a match is #500, and until that lands the endpoint returns
   * `false` for every row. The screen's matched handling is built and inert.
   */
  matched: boolean;
}

export interface BankDayDto {
  /**
   * The account this day's statement is for, as the file's `03` record carries
   * it and as #491 stores it on the statement row.
   *
   * A day entry is one account's statement for one date, so this and `date`
   * together identify it: two accounts report on the same dates, and roughly
   * half the sample production files come from the one that never reports a
   * balance. Without this field the two collapse into one row, a running balance
   * chain reads as continuous when it belongs to two accounts, and a range
   * total mixes them.
   *
   * Not optional, and not nullable. A day whose file never arrived is missing
   * for one account rather than in general, so even the state that carries no
   * file still has an account to name.
   *
   * Whether one call may span accounts, or is made per account, is #490's and
   * #492's contract question and is open as of 2026-09-16. Either way the entry
   * shape is the same.
   */
  accountNumber: string;
  /** Reporting date from the file's own content, never from its name or folder. */
  date: string;
  state: BankDayState;
  /**
   * The `files` row this day's data came from, or `null` on a `not_delivered`
   * day because there is no file to point at. It exists so a client can ask
   * #496 for that file's history rather than have this response restate it:
   * #490 answers about a day, #496 answers about a file.
   */
  fileId: string | null;
  /**
   * Why the parse failed, from #494, and `null` in every state but
   * `delivered_not_parsed`.
   *
   * Deliberately `string` and not a union of #494's three values. #494 owns the
   * set and is expected to rename and renumber it; spelling the values out here
   * would make this contract wrong the day that happens. Note the name: the
   * failure record in `files.validation_results` already has an
   * `error_category` meaning something else, so this mirrors its `error_code`.
   */
  failureCode: string | null;
  /**
   * The day's currency, from the `02` group header. It sits here rather than on
   * each movement because a day is one account's statement and carries one
   * currency for all of it.
   */
  currency: string;
  balances: BankBalanceDto[];
  transactions: BankTransactionDto[];
  /** Net of the day's transactions as an exact decimal string, credits positive. */
  dayTotal: string;
}

export interface LedgerEntryDto {
  id: string;
  /**
   * Both label pairs are carried and neither is dropped. They are not duplicates:
   * `direction` mirrors the bank's view, `flow` is the operator's. The screen
   * shows `flow` by default.
   */
  direction: 'Credit' | 'Debit';
  flow: 'Receipt' | 'Disbursement';
  category: string;
  paymentMethod: string;
  clientName: string;
  clientNumber: string;
  paymentId: string;
  externalReferenceId: string;
  /** The date the range filters on. */
  date: string;
  paymentFileReference: string;
  /** Exact decimal string as stored, always positive. */
  amount: string;
  currency: string;
  /**
   * True while the row sits in a live match group. Ending a group makes its
   * rows matchable again, so this follows the match line's `deleted_at`, not
   * the group's history.
   */
  matched: boolean;
}

const page = <T>(rows: T[]): Paginated<T> => ({
  data: rows,
  pagination: {
    page: 1,
    limit: rows.length,
    total: rows.length,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  },
});

const inRange = (date: string, { from, to }: DateRange): boolean => date >= from && date <= to;

/**
 * The fixture range. Chosen to span a month boundary, because an operator's
 * working set is a date range and not a calendar month: a deposit landing on the
 * 1st is explained by ledger rows dated in the previous month.
 */
export const FIXTURE_RANGE: DateRange = { from: '2026-06-29', to: '2026-07-06' };

/**
 * The two accounts that report. The operating account carries a running balance
 * from one day to the next; the second never reports a balance in any file, so
 * its figures are absent rather than zero.
 */
const OPERATING_ACCOUNT = '4589013920';
const ZERO_BALANCE_ACCOUNT = '4589013939';

/**
 * Every case the screen has to render: a normal day, a quiet day that parsed
 * cleanly with nothing in it, a day whose file never arrived, a day whose file
 * could not be read, the zero-balance account's day where the balance is absent
 * rather than zero, and a movement whose type code could not be placed, which
 * has neither a direction nor a label.
 *
 * Two accounts, because that is what reports. 2026-07-01 is the case a
 * date-keyed list could not hold: one account's file never arrived while the
 * other's did, so the date carries two entries in two different states.
 *
 * This is a set of cases, not a sample of the feed. #490 returns one entry per
 * account per expected business day, so a real range holds a quiet row for
 * every account on every day; repeating those here would add rows that render
 * through a path already covered and would bury the cases that do not. Each
 * account appears on the days that show something this screen has to get right.
 *
 * Ordered by date, then by account number.
 */
const BANK_DAYS: BankDayDto[] = [
  {
    accountNumber: OPERATING_ACCOUNT,
    date: '2026-06-29',
    state: 'present',
    fileId: '8f2c1a74-3b9d-4e15-9c02-6a7d4f1e8b30',
    failureCode: null,
    currency: 'USD',
    balances: [
      { typeCode: '010', label: 'Opening ledger', amount: '125000.00' },
      { typeCode: '015', label: 'Closing ledger', amount: '137500.50' },
    ],
    transactions: [
      {
        id: 'bank-1',
        direction: 'credit',
        typeCode: '142',
        typeLabel: 'ACH credit received',
        amount: '12500.50',
        customerReference: 'PAY-20260625-001',
        text: 'STEERBRIDGE STRATEGIES LLC',
        matched: false,
      },
      {
        // A code outside the ranges that certify a direction. #491 stores no
        // direction for it and no label, so the screen has to show the movement
        // without claiming which way the money went.
        id: 'bank-5',
        direction: null,
        typeCode: '890',
        typeLabel: null,
        amount: '250.00',
        customerReference: 'PAY-20260625-002',
        text: 'MISCELLANEOUS ADJUSTMENT',
        matched: false,
      },
    ],
    // The net excludes the unplaceable movement. A sum over a guessed direction
    // would read as an answer, and #490 has to say which it does.
    dayTotal: '12500.50',
  },
  {
    // The normal case, not an error. 53 of the 67 sample production files carry
    // no transactions at all, and a quiet day is a result rather than an absence
    // of one. The balances still arrive.
    accountNumber: OPERATING_ACCOUNT,
    date: '2026-06-30',
    state: 'present',
    fileId: 'a41e9d02-77c5-4b8a-9f13-2d6b0c5e9a47',
    failureCode: null,
    currency: 'USD',
    balances: [
      { typeCode: '010', label: 'Opening ledger', amount: '137500.50' },
      { typeCode: '015', label: 'Closing ledger', amount: '137500.50' },
    ],
    transactions: [],
    dayTotal: '0.00',
  },
  {
    // No file arrived and the expected-by hour has passed. Nothing is stored for
    // this day; the state is derived from the absence.
    accountNumber: OPERATING_ACCOUNT,
    date: '2026-07-01',
    state: 'not_delivered',
    fileId: null,
    failureCode: null,
    currency: 'USD',
    balances: [],
    transactions: [],
    dayTotal: '0.00',
  },
  {
    // Same date as the entry above, different account, different state. The
    // operating account's file never arrived on this day; this one's did and
    // parsed, carrying nothing but its absent balances. A missing day is missing
    // for an account, not for the range, and a list keyed on date alone reports
    // one of these two and loses the other.
    accountNumber: ZERO_BALANCE_ACCOUNT,
    date: '2026-07-01',
    state: 'present',
    fileId: 'b6f81d43-2e95-4c60-8a71-1f4c9e0b7d32',
    failureCode: null,
    currency: 'USD',
    balances: [
      { typeCode: '010', label: 'Opening ledger', amount: null },
      { typeCode: '015', label: 'Closing ledger', amount: null },
    ],
    transactions: [],
    dayTotal: '0.00',
  },
  {
    // The parse itself failed, so there is no data to show. A file that parses
    // but disagrees with its own control total is not this state: #491 stores its
    // rows and marks them, and the day is `present`.
    accountNumber: OPERATING_ACCOUNT,
    date: '2026-07-02',
    state: 'delivered_not_parsed',
    fileId: 'c7b05e31-9a24-4d6f-8e10-53f2a9c4d178',
    failureCode: 'BAI2_INVALID_CONTENT',
    currency: 'USD',
    balances: [],
    transactions: [],
    dayTotal: '0.00',
  },
  {
    // The zero-balance account. It reports transactions but never a balance in
    // any file, so the figure is absent rather than zero.
    accountNumber: ZERO_BALANCE_ACCOUNT,
    date: '2026-07-03',
    state: 'present',
    fileId: 'd9a34f68-1c07-4a92-b5e8-70c1d8f36b25',
    failureCode: null,
    currency: 'USD',
    balances: [
      { typeCode: '010', label: 'Opening ledger', amount: null },
      { typeCode: '015', label: 'Closing ledger', amount: null },
    ],
    transactions: [
      {
        id: 'bank-2',
        direction: 'credit',
        typeCode: '108',
        typeLabel: 'Deposit',
        amount: '45000.00',
        customerReference: 'PAY-20260630-002',
        text: 'GLOBAL VENTURES CAPITAL CALL',
        matched: false,
      },
      {
        id: 'bank-3',
        direction: 'debit',
        typeCode: '451',
        typeLabel: 'ACH debit',
        amount: '5000.00',
        customerReference: 'PAY-20260703-001',
        text: 'QUARTERLY DISBURSEMENT',
        matched: true,
      },
    ],
    dayTotal: '40000.00',
  },
  {
    // A day whose only transaction is matched. With matched rows hidden it has
    // nothing left to show, and it must still not claim the bank sent nothing.
    // 2026-07-04 fell on a Saturday, so the next expected business day is the 6th.
    accountNumber: OPERATING_ACCOUNT,
    date: '2026-07-06',
    state: 'present',
    fileId: 'e50b2c19-6d83-4f07-a1c4-9b3e7d52f6a8',
    failureCode: null,
    currency: 'USD',
    balances: [
      { typeCode: '010', label: 'Opening ledger', amount: '177500.50' },
      { typeCode: '015', label: 'Closing ledger', amount: '178500.50' },
    ],
    transactions: [
      {
        id: 'bank-4',
        direction: 'credit',
        typeCode: '142',
        typeLabel: 'ACH credit received',
        amount: '1000.00',
        customerReference: 'PAY-20260628-009',
        text: 'RECURRING TRANSFER',
        matched: true,
      },
    ],
    dayTotal: '1000.00',
  },
];

const LEDGER_ENTRIES: LedgerEntryDto[] = [
  {
    // Dated in June against a bank row on 2026-06-29: the two sides differ by
    // days, in both directions, and a row must not read as an orphan because of it.
    id: 'ledger-1',
    direction: 'Credit',
    flow: 'Receipt',
    category: 'Receipt',
    paymentMethod: 'ACH',
    clientName: 'SteerBridge Strategies',
    clientNumber: 'CLT-002',
    paymentId: 'PMT-20260625-001',
    externalReferenceId: 'PAY-20260625-001',
    date: '2026-06-29',
    paymentFileReference: 'PAY-20260625-001.csv',
    amount: '12500.50',
    currency: 'USD',
    matched: false,
  },
  {
    id: 'ledger-2',
    direction: 'Credit',
    flow: 'Receipt',
    category: 'Receipt',
    paymentMethod: 'Wire',
    clientName: 'Global Ventures',
    clientNumber: 'CLT-005',
    paymentId: 'PMT-20260630-002',
    externalReferenceId: 'PAY-20260630-002',
    date: '2026-06-30',
    paymentFileReference: 'PAY-20260630-002.csv',
    amount: '45000.00',
    currency: 'USD',
    matched: false,
  },
  {
    id: 'ledger-3',
    direction: 'Debit',
    flow: 'Disbursement',
    category: 'Payment',
    paymentMethod: 'ACH',
    clientName: 'Sunrise Properties',
    clientNumber: 'CLT-003',
    paymentId: 'PMT-20260703-001',
    externalReferenceId: 'PAY-20260703-001',
    date: '2026-07-03',
    paymentFileReference: 'PAY-20260703-001.csv',
    amount: '5000.00',
    currency: 'USD',
    matched: true,
  },
  {
    // Siphon in: the payment file lands days before the money does, so the ledger
    // carries a declared receipt with no bank row yet against it. This is the named
    // use case for hand-added entries and the category the design displays; without
    // a row like it nothing exercises that label.
    id: 'ledger-5',
    direction: 'Credit',
    flow: 'Receipt',
    category: 'Siphon In',
    paymentMethod: 'Internal',
    clientName: 'Internal Transfer',
    clientNumber: 'INT-001',
    paymentId: 'SIPH-20260701-001',
    externalReferenceId: 'SIPH-20260701-001',
    date: '2026-07-01',
    paymentFileReference: 'SIPH-20260701-001',
    amount: '5000.00',
    currency: 'USD',
    matched: false,
  },
  {
    // A fourth decimal place, which is what `decimal(18,4)` storage allows and
    // what a `Number.parseFloat(...).toFixed(2)` round trip would silently drop.
    id: 'ledger-4',
    direction: 'Debit',
    flow: 'Disbursement',
    category: 'Payment',
    paymentMethod: 'Check',
    clientName: 'SafeGuard Insurance',
    clientNumber: 'CLT-004',
    paymentId: 'PMT-20260702-001',
    externalReferenceId: 'PAY-20260702-001',
    date: '2026-07-02',
    paymentFileReference: 'PAY-20260702-001.csv',
    amount: '2525.7525',
    currency: 'USD',
    matched: false,
  },
];

/**
 * Bank activity for a date range, one entry per account per expected day.
 *
 * Ordered by date, then by account number, so the order is the endpoint's rather
 * than the order the fixtures happen to be declared in. Replaced in the
 * side-by-side story by a call to the bank read API. The signature is the
 * endpoint's, so only this body changes.
 */
export const getBankDays = async (range: DateRange): Promise<Paginated<BankDayDto>> =>
  page(
    BANK_DAYS.filter((day) => inRange(day.date, range)).sort(
      (a, b) => a.date.localeCompare(b.date) || a.accountNumber.localeCompare(b.accountNumber)
    )
  );

/**
 * Platform ledger rows for a date range.
 *
 * Replaced in the side-by-side story by a call to the ledger read API.
 */
export const getLedgerEntries = async (range: DateRange): Promise<Paginated<LedgerEntryDto>> =>
  page(LEDGER_ENTRIES.filter((entry) => inRange(entry.date, range)));
